import {
  createContext,
  useMemo,
} from 'react';
import { PackageInformationResponse, PackageSessionResponse } from '~/types/api';
import { useOptions } from '~/hooks/useOptions';
import { InferredBookingFlowOptions } from '~/types/options';
import useSessionId from '~/hooks/bookingFlow/useSessionId';
import { useInformationForm } from '~/hooks/bookingFlow/useInformationForm';
import usePackageSession from '~/hooks/bookingFlow/usePackageSession';

type BookingFlowSummaryContextType = {
  packageInformationData?: PackageInformationResponse;
  packageSessionData?: PackageSessionResponse;
};

export const BookingFlowSummaryContext = createContext<BookingFlowSummaryContextType>({
  packageInformationData: undefined,
  packageSessionData: undefined,
});

type Props = {
  children: React.ReactNode;
};

export default function BookingFlowSummaryProvider({
  children,
}: Props) {
  // Retrieve the options passed to the embed, for example the locale, package id, etc.
  const options = useOptions() as InferredBookingFlowOptions;

  // Retrieve the session ID from the URL, if it is set.
  const { sessionId } = useSessionId();

  if (!sessionId) {
    throw new Error('Session ID is not set');
  }

  // Retrieve information form data from the backend, this can be undefined if there's
  // no information in the session yet.
  const {
    data: packageInformationData,
    // error: informationFormError,
  } = useInformationForm(sessionId);

  const {
    data: packageSessionData,
  } = usePackageSession({
    sessionId,
    locale: options.locale,
  });

  const value = useMemo(() => {
    return {
      packageInformationData,
      packageSessionData,
    };
  }, [
    packageInformationData,
    packageSessionData,
  ]);

  return (
    <BookingFlowSummaryContext.Provider value={value}>
      {children}
    </BookingFlowSummaryContext.Provider>
  );
}
